<template>
  <!-- Guests: they can't buy, but a free account is one click away and
       comes with 1 more conversion. Signing up upgrades the guest account
       in place, so this result stays theirs. -->
  <section
    v-if="mode === 'signup'"
    class="result-offer"
    aria-labelledby="result-offer-title"
  >
    <div class="offer-text">
      <v-icon small class="offer-icon">mdi-gift-outline</v-icon>
      <strong id="result-offer-title" class="offer-title">
        Love the result? Get 1 more free.
      </strong>
      <span class="offer-sub">
        That was your free conversion. Create a free account - no card
        needed.
      </span>
    </div>

    <div class="offer-actions">
      <GoogleAuthButton
        mode="register"
        :divider="false"
        :button-width="240"
        stay-on-upgrade
      />
      <v-btn
        depressed
        class="offer-btn offer-btn--solid offer-btn--tall"
        :to="signupLink"
      >
        {{ hasGoogle ? "Sign up with email" : "Sign up free - get 1 more" }}
      </v-btn>
    </div>
  </section>

  <section v-else class="result-offer" aria-labelledby="result-offer-title">
    <div class="offer-text">
      <v-icon small class="offer-icon">mdi-auto-fix</v-icon>
      <strong id="result-offer-title" class="offer-title">
        Love the result? Keep going.
      </strong>
      <span class="offer-sub">
        That was your last free conversion. Any purchase unlocks everything -
        Extreme realism, 4x output and watermark-free downloads.
      </span>
    </div>

    <!-- Phones: two buttons instead of six - the full pack/plan list lives
         in the paywall dialog, which already lays out well on mobile. -->
    <div
      v-if="$vuetify.breakpoint.xsOnly"
      class="offer-actions offer-actions--compact"
    >
      <v-btn
        depressed
        class="offer-btn offer-btn--solid"
        @click="compactDialog = true"
      >
        One-time packs · from ${{ shortPrice(packPrice(packs[0].credits)) }}
      </v-btn>
      <v-btn
        v-if="paidPlans.length"
        depressed
        class="offer-btn"
        @click="compactDialog = true"
      >
        Monthly plans · from ${{ shortPrice(Number(paidPlans[0].price_monthly).toFixed(2)) }}/mo
      </v-btn>
      <SubscribeDialog v-model="compactDialog" />
    </div>

    <div v-else class="offer-actions">
      <!-- One-time packs: the hero offer for one-off needs -->
      <div class="offer-group">
        <span class="offer-label">One-time</span>
        <v-btn
          v-for="(pack, index) in packs"
          :key="pack.credits"
          depressed
          class="offer-btn"
          :class="index === 0 ? 'offer-btn--solid' : 'offer-btn--ghost'"
          :title="pack.first ? 'One-time welcome pack - first purchase only' : ''"
          :loading="packLoading === pack.credits"
          :disabled="packLoading !== null"
          @click="buyPack(pack)"
        >
          {{ pack.first ? "First pack: " : "" }}≈ {{ pack.photos }} conversions ·
          ${{ shortPrice(packPrice(pack.credits)) }}
        </v-btn>
      </div>

      <!-- Monthly plans -->
      <div v-if="paidPlans.length" class="offer-group">
        <span class="offer-label">Monthly</span>
        <v-btn
          v-for="plan in paidPlans"
          :key="plan.id"
          depressed
          class="offer-btn offer-btn--ghost"
          :title="'≈ ' + highCount(plan) + ' High-realism conversions / month'"
          @click="choosePlan(plan)"
        >
          {{ planTitle(plan) }} · ${{
            Number(plan.price_monthly).toFixed(2)
          }}/mo
        </v-btn>
      </div>
    </div>

    <v-alert v-if="packError" type="error" dense outlined class="mt-3 mb-0">
      {{ packError }}
    </v-alert>
  </section>
</template>

<script>
// Result-page offer, shown to members who just spent their last free
// conversion and can't afford another (mode "buy"), or to guests who used
// their free one (mode "signup"): the moment they're looking at a
// result they like. Deliberately compact - one line of copy and a row of
// small buttons - so the result stays the star. Packs first (most visitors
// have one batch of images to fix), plans beside them. A pack bought here
// returns the buyer to the Enhance page, ready for their next image; a
// cancelled checkout returns them to this result.
import GoogleAuthButton from "@shared/components/GoogleAuthButton.vue";
import { registerFirst } from "@shared/utils/memberFunnel";
import upgradeOffer from "../mixins/upgradeOffer";
import SubscribeDialog from "./SubscribeDialog.vue";

export default {
  name: "ResultOffer",
  components: { GoogleAuthButton, SubscribeDialog },
  mixins: [upgradeOffer],
  props: {
    // "buy": members out of free conversions (packs + plans);
    // "signup": guests who used their free one (free account, +1).
    mode: { type: String, default: "buy" }
  },
  data() {
    return { compactDialog: false };
  },
  computed: {
    hasGoogle() {
      const config = this.$store.state.publicConfig;
      return !!(config && config.google_auth && config.google_auth.client_id);
    },
    // Email sign-up returns the new member to this result.
    signupLink() {
      return registerFirst(this.$route.fullPath);
    }
  },
  mounted() {
    if (this.mode === "buy") this.fetchPlans();
  },
  methods: {
    checkoutUrls() {
      return {
        successUrl: window.location.origin + "/?credits=added",
        cancelUrl: window.location.href
      };
    },
    // "5.00" -> "5", "7.14" -> "7.14": whole-dollar packs read cleaner.
    shortPrice(value) {
      return String(value).replace(/\.00$/, "");
    }
  }
};
</script>

<style scoped>
/* A slim but distinct strip: brand-glow outline with amber call-to-action
   buttons in sentence case, deliberately unlike the page's purple,
   uppercase tool buttons. */
.result-offer {
  margin: 4px 0 14px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid rgba(168, 85, 247, 0.45);
  background: radial-gradient(
      120% 160% at 0% 0%,
      rgba(168, 85, 247, 0.16),
      transparent 55%
    ),
    var(--bg-card, #15151d);
  box-shadow: 0 6px 24px rgba(168, 85, 247, 0.12);
}

.offer-text {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-bottom: 10px;
}

.offer-icon {
  color: #ec4899 !important;
  align-self: center;
}

.offer-title {
  font-size: 1rem;
  font-weight: 800;
  color: var(--text-primary, #ececf1);
  letter-spacing: normal;
}

.offer-sub {
  color: var(--text-secondary, #a1a1ae);
  font-size: 0.9rem;
}

/* Two aligned rows: a fixed-width label column, then the buttons, so
   "One-time" and "Monthly" line up and every button starts at the same x. */
.offer-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}

.offer-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.offer-label {
  flex: 0 0 72px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-secondary, #a1a1ae);
}

/* Warm amber->orange fill with dark text: the one colour on the page that
   isn't the purple/pink of the tool buttons (contrast >= 6:1 throughout). */
.offer-btn.v-btn {
  height: 36px;
  min-width: 158px;
  padding: 0 16px;
  text-transform: none;
  letter-spacing: normal;
  font-size: 0.85rem;
  font-weight: 800;
  /* Pure black on amber -> light orange stays >= 9:1 contrast end to end. */
  color: #000000 !important;
  background: linear-gradient(135deg, #fcd34d 0%, #fb923c 100%) !important;
  transition: filter 0.15s ease, box-shadow 0.15s ease;
}

.offer-btn.v-btn:hover {
  filter: brightness(1.08);
}

/* Next to Google's fixed 40px button (signup strip). */
.offer-btn.offer-btn--tall.v-btn {
  height: 40px;
}

/* The $5 pack leads: a soft glow on top of the shared fill. */
.offer-btn.offer-btn--solid {
  box-shadow: 0 0 0 2px rgba(251, 191, 36, 0.35),
    0 4px 14px rgba(249, 115, 22, 0.45);
}

/* Phone-compact: two full-width buttons stacked. */
.offer-actions--compact {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}

.offer-actions--compact .offer-btn.v-btn {
  width: 100%;
}
</style>
